/**
 * Registro de baixas — lista todas as parcelas que foram baixadas (pagas),
 * independente de origem: manual (operador registrou na tela quando o webhook
 * falhou) ou automatica (webhook do gateway PIX, ex: Cora). Permite estornar
 * inline em caso de erro do operador (com motivo, pra auditoria).
 */

import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Badge,
  Button,
  DataTable,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  useToast,
  type DataTableColumn,
} from "@payflow/ui";
import {
  baixasService,
  type Baixa,
  type OrigemBaixa,
} from "../../services/baixas.service";

export function BaixasPage() {
  const { t } = useTranslation();
  const { toast } = useToast();

  const [items, setItems] = useState<Baixa[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [onlyActive, setOnlyActive] = useState(true);
  const [origem, setOrigem] = useState<"" | OrigemBaixa>("");

  const [reverseTarget, setReverseTarget] = useState<Baixa | null>(null);
  const [reverseMotivo, setReverseMotivo] = useState("");
  const [reversing, setReversing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await baixasService.list({
        page,
        limit: pageSize,
        search: search.trim() || undefined,
        onlyActive,
        origem: origem || undefined,
      });
      setItems(res.data);
      setTotal(res.meta.total);
    } catch (err) {
      toast({
        variant: "error",
        title: t("baixas.load_error"),
        description: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search, onlyActive, origem, toast, t]);

  useEffect(() => {
    load();
  }, [load]);

  const handleReverse = async () => {
    if (!reverseTarget) return;
    setReversing(true);
    try {
      await baixasService.reverse(reverseTarget.parcelaId, reverseMotivo.trim() || undefined);
      toast({ variant: "success", title: t("baixas.reversed_success") });
      setReverseTarget(null);
      setReverseMotivo("");
      load();
    } catch (err) {
      toast({
        variant: "error",
        title: t("baixas.reverse_error"),
        description: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setReversing(false);
    }
  };

  const columns: DataTableColumn<Baixa>[] = [
    {
      key: "cliente",
      header: t("baixas.col_client"),
      render: (row) => (
        <div className="flex flex-col">
          <span className="font-medium text-foreground">{row.clienteNome}</span>
          <span className="text-xs text-muted-foreground">
            {t("baixas.installment_short", { n: row.parcelaNumero })}
          </span>
        </div>
      ),
    },
    {
      key: "origem",
      header: t("baixas.col_origin"),
      render: (row) => {
        if (row.origem === "manual") {
          return (
            <Badge variant="warning" className="whitespace-nowrap">
              {t("baixas.origin_manual")}
            </Badge>
          );
        }
        if (row.origem === "conciliacao") {
          return (
            <Badge
              variant="default"
              className="whitespace-nowrap bg-purple-100 text-purple-800 border border-purple-200"
            >
              {t("baixas.origin_reconciliation")}
            </Badge>
          );
        }
        return (
          <Badge variant="info" className="whitespace-nowrap">
            {t("baixas.origin_automatic")}
          </Badge>
        );
      },
    },
    {
      key: "funcionario",
      header: t("baixas.col_employee"),
      render: (row) =>
        row.funcionarioNome ? (
          <div className="flex flex-col">
            <span className="text-foreground">{row.funcionarioNome}</span>
            {row.funcionarioEmail && (
              <span className="text-xs text-muted-foreground">{row.funcionarioEmail}</span>
            )}
          </div>
        ) : (
          <span className="text-xs italic text-muted-foreground">
            {row.origem === "conciliacao"
              ? t("baixas.no_operator_reconciliation")
              : t("baixas.no_operator")}
          </span>
        ),
    },
    {
      key: "valor",
      header: t("baixas.col_value"),
      render: (row) => (
        <span className="font-semibold text-foreground">{fmtMoney(row.parcelaValor)}</span>
      ),
    },
    {
      key: "formaPagamento",
      header: t("baixas.col_method"),
      render: (row) => (
        <Badge variant="outline" className="capitalize">
          {row.formaPagamento || "-"}
        </Badge>
      ),
    },
    {
      key: "registradoEm",
      header: t("baixas.col_datetime"),
      render: (row) => (
        <div className="flex flex-col">
          <span className="text-foreground">{fmtDateTime(row.registradoEm)}</span>
          {row.dataPagamento && row.dataPagamento !== row.registradoEm.slice(0, 10) && (
            <span className="text-xs text-muted-foreground">
              {t("baixas.payment_date", { date: fmtDate(row.dataPagamento) })}
            </span>
          )}
        </div>
      ),
    },
    {
      key: "status",
      header: t("baixas.col_status"),
      render: (row) =>
        row.estornado ? (
          <div className="flex flex-col">
            <Badge variant="error">{t("baixas.status_reversed")}</Badge>
            {row.estornadoEm && (
              <span className="mt-1 text-[11px] text-muted-foreground">
                {fmtDateTime(row.estornadoEm)}
                {row.estornadoPorNome ? ` — ${row.estornadoPorNome}` : ""}
              </span>
            )}
          </div>
        ) : (
          <Badge variant="success">{t("baixas.status_active")}</Badge>
        ),
    },
    {
      key: "actions",
      header: "",
      className: "text-right",
      render: (row) =>
        row.estornado ? null : (
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              setReverseTarget(row);
              setReverseMotivo("");
            }}
            title={t("baixas.reverse_action")}
          >
            <ReverseIcon />
            <span className="ml-1 hidden sm:inline">{t("baixas.reverse_action")}</span>
          </Button>
        ),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">{t("baixas.title")}</h1>
          <p className="text-sm text-muted-foreground">{t("baixas.subtitle")}</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <select
            value={origem}
            onChange={(e) => {
              setOrigem(e.target.value as "" | OrigemBaixa);
              setPage(1);
            }}
            className="h-9 min-w-[160px] rounded-lg border border-input bg-card px-3 text-sm text-foreground"
          >
            <option value="">{t("baixas.filter_all_origins")}</option>
            <option value="automatica">{t("baixas.origin_automatic")}</option>
            <option value="manual">{t("baixas.origin_manual")}</option>
            <option value="conciliacao">{t("baixas.origin_reconciliation")}</option>
          </select>
          <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-foreground">
            <input
              type="checkbox"
              checked={onlyActive}
              onChange={(e) => {
                setOnlyActive(e.target.checked);
                setPage(1);
              }}
              className="h-4 w-4 accent-primary-600"
            />
            {t("baixas.only_active")}
          </label>
        </div>
      </div>

      <DataTable
        columns={columns}
        data={items}
        total={total}
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(1);
        }}
        loading={loading}
        searchValue={search}
        onSearchChange={(v) => {
          setSearch(v);
          setPage(1);
        }}
        searchPlaceholder={t("baixas.search_placeholder")}
        emptyMessage={t("baixas.empty")}
      />

      <Dialog
        open={!!reverseTarget}
        onOpenChange={(open) => {
          if (!open) {
            setReverseTarget(null);
            setReverseMotivo("");
          }
        }}
      >
        <DialogContent size="sm">
          <DialogHeader>
            <DialogTitle>{t("baixas.reverse_title")}</DialogTitle>
          </DialogHeader>
          {reverseTarget && (
            <div className="space-y-4 py-2">
              <p className="text-sm text-muted-foreground">
                {t("baixas.reverse_confirm", {
                  cliente: reverseTarget.clienteNome,
                  valor: fmtMoney(reverseTarget.parcelaValor),
                })}
              </p>
              <div>
                <label className="mb-1 block text-sm font-medium text-foreground">
                  {t("baixas.reason_label")}
                </label>
                <Input
                  placeholder={t("baixas.reason_placeholder")}
                  value={reverseMotivo}
                  onChange={(e) => setReverseMotivo(e.target.value)}
                  maxLength={255}
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  {t("baixas.reason_hint")}
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setReverseTarget(null)}>
              {t("common.cancel")}
            </Button>
            <Button
              variant="destructive"
              onClick={handleReverse}
              disabled={reversing}
            >
              {reversing ? t("common.loading") : t("baixas.confirm_reverse")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function fmtMoney(v: string | number): string {
  const n = typeof v === "string" ? parseFloat(v) : v;
  if (!Number.isFinite(n)) return "R$ 0,00";
  return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function fmtDate(iso: string): string {
  const d = new Date(iso.includes("T") ? iso : iso + "T12:00:00");
  return d.toLocaleDateString("pt-BR");
}

function fmtDateTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function ReverseIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
      <path d="M3 3v5h5" />
    </svg>
  );
}
